import { Badge } from "../../components/Badge";
import { Card } from "../../components/Card";
import { StatValue } from "../../components/StatValue";
import {
  MODE_LABEL,
  PERSONA_LABEL,
  type CatalogueSeed,
  isReachable,
  priceToday,
} from "../../config/catalogue";
import type { BuildStatus } from "../../services/api/schemas";
import type { AccessDecision } from "./access";
import styles from "./ModelCard.module.css";

interface Props {
  /** Accepts a config seed or a fetched entry — the card needs no capabilities. */
  model: CatalogueSeed;
  /** Where the card links. Undefined renders it inert. */
  to?: string;
  /**
   * How the viewer stands with this model. Omitted on the public landing page,
   * where nobody is signed in and the price is the only honest footer.
   */
  access?: AccessDecision;
}

const BUILD_BADGE: Record<BuildStatus, { label: string; tone: "signal" | "neutral" }> = {
  live: { label: "Live", tone: "signal" },
  beta: { label: "Beta", tone: "signal" },
  soon: { label: "Coming soon", tone: "neutral" },
  "off-roadmap": { label: "Not planned", tone: "neutral" },
};

/**
 * A single cell of the sport × mode grid.
 *
 * Everything shown here comes off the catalogue entry — including whether a
 * price appears at all. An unbuilt model renders "Not yet available" where the
 * price would go, so the card cannot imply something is for sale that isn't.
 */
export function ModelCard({ model, to, access }: Props) {
  const reachable = isReachable(model);
  const badge = BUILD_BADGE[model.build];

  /**
   * The footer answers "what does this cost me?" — and the honest answer depends
   * on where the viewer stands. Showing a price to someone who already
   * subscribes is the small lie that makes people distrust the whole page.
   */
  function renderFooter() {
    if (!reachable) return <span className={styles.unavailable}>Not yet available</span>;

    if (access?.allowed) {
      const via =
        access.via === "model-sub" ? "Your subscription" : `Included with ${access.via}`;
      return (
        <>
          <span className={styles.included}>{via}</span>
          <span className={styles.open}>Open →</span>
        </>
      );
    }

    const price = model.purchasable ? priceToday(model) : null;
    if (price) {
      /* Lead with what they'll be charged today and name the revert price
         beside it. Showing only the promo price would leave the customer to
         find the standard rate on an invoice, which Terms §6 says we won't do.
         The charge-today number is the factual one, so it takes the data tone.

         `price.now` is PER MONTH and is exactly what the card is charged. The
         unit label has been wrong here before in the expensive direction: it
         said "/mo" against a WEEKLY figure until 2026-08-06, so a $7.99 weekly
         rate read as $7.99 a month when the real charge was $34.62. An
         understated headline price, on the most-seen surface in the product, is
         the direction of error the ACL actually cares about.

         Prices became monthly on 2026-08-31, which retires that whole class of
         bug: there is one number, the unit is "/month", and the separate
         "billed monthly at" line is gone because it would now just repeat the
         figure immediately to its left. */
      return (
        <span className={styles.price}>
          <StatValue tone="data">${price.now.toFixed(2)}</StatValue>
          <span className={styles.priceUnit}> /month</span>
          {price.note && <span className={styles.priceNote}>{price.note}</span>}
        </span>
      );
    }

    return <span className={styles.unavailable}>Not yet available</span>;
  }

  return (
    <Card to={reachable ? to : undefined} disabled={!reachable} className={styles.card}>
      <div className={`${styles.top} ${reachable ? "" : styles.dim}`}>
        <div className={styles.labels}>
          <span className={styles.mode}>
            {MODE_LABEL[model.mode]} · {PERSONA_LABEL[model.persona]}
          </span>
          <span className={styles.name}>{model.name}</span>
        </div>
        <Badge tone={badge.tone}>{badge.label}</Badge>
      </div>

      <p className={styles.blurb}>{model.blurb}</p>

      {model.sampleNote && <p className={styles.note}>{model.sampleNote}</p>}
      {/* No status note (Ned, 2026-08-06). This used to render, for unbuilt
          models, sentences like "Model's trained. The chat surface lands next."
          The Badge already says "Coming soon", which is the part a reader
          needs; the sentence underneath was a delivery commitment on a page
          that cannot keep one. The field has since been removed from the
          catalogue and the API schema entirely. */}

      <div className={styles.foot}>{renderFooter()}</div>
    </Card>
  );
}
